import { faBadgePercent } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clsx } from "clsx";
import { useLocation, useNavigate } from "react-router-dom";

import { T } from "@repo/transifex";
import type { AiColors } from "@repo/types";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { ItemLayout } from "~/components/ItemLayout";
import { useSendWebEvent } from "~/hooks";
import { useDateBadge } from "~/hooks/use-date-badge";
import { useAppContext } from "~/providers/app";
import { basketActions } from "~/providers/store/basket";
import { modalVariantsActions } from "~/providers/store/modal-variants";
import { isProductAvailable } from "~/utils/order-deadlines/is-product-available";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import { getUnitSystemLabel } from "~/utils/get-unit-system-label";
import { Badge, BadgeStock } from "~/components/Badge";
import { type DisplayProduct } from "~/providers/store/basket/hooks/use-display-products";
import { type UnitSystem } from "~/api/products/types";
import { shouldShowListStrikethrough } from "~/utils/hybrid-billing-display";

type ProductItemProps = {
  product: DisplayProduct;
  disabled: boolean;
  unitSystem?: UnitSystem;
};

export const ProductItem = ({
  product,
  disabled,
  unitSystem
}: ProductItemProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const sendWebEvent = useSendWebEvent();

  const {
    hardware: {
      scales: { scale, unit: scaleUnit, decimals: scaleDecimals }
    }
  } = useAppContext();

  const { data: till, status } = useCurrentTill();
  const { timezone } = useCurrentTillLocalisation();

  const {
    name,
    displayPrice,
    displayPriceOriginal,
    deadline,
    onset,
    discount,
    stockStatus,
    disabled: isDisabledByStockOrLimit
  } = product;

  const { disabled: disabledByTime, badge: badgeTime } = useDateBadge(
    deadline,
    onset,
    timezone
  );

  const isDisabled = disabledByTime || disabled || isDisabledByStockOrLimit;

  const shouldDisplayDiscount = shouldShowListStrikethrough(product.discount);

  const hasVariants = product.options.length > 0;
  const isWeightedProduct = product.unitSystem !== "piece";

  const unitSystemLabel = getUnitSystemLabel(unitSystem);

  if (status !== "success") return null;

  const hasColor =
    (hasAttendedSellConfig(till) || till.type === "self-service") &&
    till.config.hasProductColors;

  if (!isProductAvailable({ deadline, onset: null })) {
    return null;
  }

  return (
    <ItemLayout
      badgeRight={badgeTime ? <Badge>{badgeTime}</Badge> : undefined}
      badgeLeft={<BadgeStock stockStatus={stockStatus} />}
      className={clsx(
        "flex size-full flex-1 flex-col justify-end gap-2 bg-ai-gray-primary p-4 text-left text-lg text-ai-gray-secondary",
        hasColor &&
          typeof product.color !== "undefined" &&
          getColorClassNames(product.color)
      )}
      disabled={isDisabled}
      onClick={() => {
        switch (true) {
          case isWeightedProduct: {
            if (scale) {
              const scaleData = "model" in scale ? scale.scale : scale;
              sendWebEvent({
                type: "SCALE_CONNECT_REQUEST",
                payload: {
                  ...scaleData,
                  unit: scaleUnit,
                  decimals: scaleDecimals
                }
              });
            }
            navigate(`${pathname}/weight-product/${product.id}`);
            break;
          }
          case hasVariants:
            modalVariantsActions.init({
              productId: product.id,
              options: product.options.map((option) => option.id)
            });
            navigate(`/pos/product/${product.id}`);
            break;
          default:
            basketActions.addRegular(
              product.id,
              !product.discount?.isAllowanceOver
            );
        }
      }}
    >
      <span className="line-clamp-2 text-2xl leading-tight">{name}</span>
      {/* Product is eligible for hybrid billing discount */}
      {discount ? (
        <div className="absolute right-2 top-2">
          <FontAwesomeIcon
            icon={faBadgePercent}
            className={clsx({
              "text-interface-negative-2": discount.allowanceType === null,
              "text-interface-positive-3":
                discount.allowanceType !== null && !discount.isAllowanceOver,
              "text-text-disabled":
                discount.allowanceType !== null && discount.isAllowanceOver
            })}
          />
        </div>
      ) : null}
      {hasVariants ? (
        <span className="text-base opacity-70">
          <T _str="Choose variant" />
        </span>
      ) : (
        <div className="text-base">
          {till.moduleConfig.hidePrices ? null : (
            <span>
              {shouldDisplayDiscount ? (
                <span className="mr-2 line-through opacity-70">
                  {displayPriceOriginal}
                </span>
              ) : null}
              <span>{displayPrice}</span>
            </span>
          )}

          {isWeightedProduct ? (
            <span className="ml-2 opacity-70">{unitSystemLabel ?? ""}</span>
          ) : null}
        </div>
      )}
    </ItemLayout>
  );
};

const getColorClassNames = (color: AiColors) => {
  const colorMap: Record<AiColors, string> = {
    lime: "!bg-ai-lime-primary !text-ai-lime-secondary",
    mint: "!bg-ai-mint-primary !text-ai-mint-secondary",
    teal: "!bg-ai-teal-primary !text-ai-teal-secondary",
    blue: "!bg-ai-blue-primary !text-ai-blue-secondary",
    navy: "!bg-ai-navy-primary !text-ai-navy-secondary",
    purple: "!bg-ai-purple-primary !text-ai-purple-secondary",
    pink: "!bg-ai-pink-primary !text-ai-pink-secondary",
    red: "!bg-ai-red-primary !text-ai-red-secondary",
    brown: "!bg-ai-brown-primary !text-ai-brown-secondary",
    orange: "!bg-ai-orange-primary !text-ai-orange-secondary",
    yellow: "!bg-ai-yellow-primary !text-ai-yellow-secondary",
    gray: "!bg-ai-gray-primary !text-ai-gray-secondary"
  };

  return colorMap[color] || "";
};
